import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { fetchMergeReview, mergeCustomers } from '../api';
import './mergecustomersmodal.css';

const FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'address', label: 'Address' },
];

const COUNT_LABELS = [
  ['work_orders', 'work order'],
  ['quotes', 'quote'],
  ['jobs', 'job'],
  ['appointments', 'appointment'],
  ['subscriptions', 'staff notification subscription'],
  ['inquiries', 'sales inquiry', 'sales inquiries'],
  ['crm_contacts', 'sales contact'],
];

const pluralize = (count, word, plural) => `${count} ${count === 1 ? word : (plural || `${word}s`)}`;
const normEmail = (email) => (email || '').trim().toLowerCase();
// Digits only, without a leading US country code — matches the server's normalize_phone.
const normPhone = (phone) => {
  const text = (phone || '').trim();
  let digits = text.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  return digits || text.toLowerCase();
};
const normName = (name) => (name || '').split(/\s+/).filter(Boolean).join(' ').toLowerCase();

// The selected row is highlighted, not just its radio, so the choice is obvious on phones.
const optionClass = (selected) => `merge-option${selected ? ' merge-option-selected' : ''}`;

const countsText = (counts) => {
  const parts = COUNT_LABELS.slice(0, 4)
    .filter(([key]) => counts[key])
    .map(([key, word, plural]) => pluralize(counts[key], word, plural));
  return parts.length ? parts.join(', ') : 'no records';
};

// Kept customer first, then the rest by id — the same order the server uses.
const ordered = (customers, keepId) => [
  ...customers.filter((c) => c.id === keepId),
  ...customers.filter((c) => c.id !== keepId),
];

// The kept customer's details, with blanks filled from the others and notes combined.
const defaultValues = (customers, keepId) => {
  const list = ordered(customers, keepId);
  const values = {};
  FIELDS.forEach(({ key }) => {
    values[key] = (list.find((c) => (c[key] || '').trim()) || {})[key] || '';
  });
  const notes = [];
  list.forEach((c) => {
    const note = (c.notes || '').trim();
    if (note && !notes.includes(note)) notes.push(note);
  });
  values.notes = notes.join('\n\n');
  return values;
};

// Every email (or phone) in the group other than the kept one — saved as additional
// emails (or phones). Mirrors clean_additional_emails / clean_additional_phones.
const leftovers = (customers, keepId, kept, field, listField, normalize) => {
  const seen = new Set([normalize(kept)].filter(Boolean));
  const result = [];
  ordered(customers, keepId).forEach((c) => {
    [c[field], ...(c[listField] || [])].forEach((value) => {
      const key = normalize(value);
      if (key && !seen.has(key)) {
        seen.add(key);
        result.push(value.trim());
      }
    });
  });
  return result;
};

const additionalEmails = (customers, keepId, keptEmail) => (
  leftovers(customers, keepId, keptEmail, 'email', 'additional_emails', normEmail)
);

const additionalPhones = (customers, keepId, keptPhone) => (
  leftovers(customers, keepId, keptPhone, 'phone', 'additional_phones', normPhone)
);

// Pick the customer with the most work orders, then the oldest.
const defaultKeepId = (customers) => [...customers].sort(
  (a, b) => (b.counts.work_orders - a.counts.work_orders) || (a.id - b.id),
)[0].id;

/**
 * Admin Settings → Duplicate Customers → Merge…. Choose the customer to keep and its
 * details, review exactly what will move and be deleted, then type the name to confirm.
 * Everything happens in one server request; if the customers changed since the review,
 * the server refuses and this shows the fresh review.
 */
const MergeCustomersModal = ({ customerIds, onClose, onMerged }) => {
  const [review, setReview] = useState(null);
  const [step, setStep] = useState('choose');
  const [keepId, setKeepId] = useState(null);
  const [values, setValues] = useState({});
  const [customFields, setCustomFields] = useState({});
  const [confirmName, setConfirmName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(false);

  const startFrom = (data, preferredKeepId) => {
    const ids = data.customers.map((c) => c.id);
    const keep = ids.includes(preferredKeepId) ? preferredKeepId : defaultKeepId(data.customers);
    setReview(data);
    setKeepId(keep);
    setValues(defaultValues(data.customers, keep));
    setCustomFields({});
    setConfirmName('');
    setStep('choose');
  };

  useEffect(() => {
    fetchMergeReview(customerIds)
      .then((data) => startFrom(data, null))
      .catch((err) => setError(err.response?.data?.error || 'Failed to load these customers.'))
      .finally(() => setLoading(false));
  }, [customerIds]);

  const chooseKeep = (id) => {
    setKeepId(id);
    setValues(defaultValues(review.customers, id));
    setCustomFields({});
  };

  const setValue = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

  const optedOut = new Set(review ? review.opted_out_emails : []);
  const isOptedOut = (email) => optedOut.has(normEmail(email));

  const handleMerge = async () => {
    setMerging(true);
    setError('');
    try {
      const result = await mergeCustomers({
        keepId,
        mergeIds: review.customers.map((c) => c.id).filter((id) => id !== keepId),
        fields: values,
        fingerprint: review.fingerprint,
        confirmName,
      });
      onMerged(result.customer, result.merged_count);
    } catch (err) {
      const data = err.response?.data || {};
      if (err.response?.status === 409 && data.review) {
        startFrom(data.review, keepId);
        setError(`${data.error} Check the details again — nothing was merged.`);
      } else {
        setError(data.error || 'The merge failed. Nothing was changed.');
      }
    } finally {
      setMerging(false);
    }
  };

  const renderFieldChoice = ({ key, label }) => {
    const options = [];
    review.customers.forEach((c) => {
      const value = (c[key] || '').trim();
      if (!value) return;
      const existing = options.find((o) => o.value === value);
      if (existing) existing.sources.push(c);
      else options.push({ value, sources: [c] });
    });
    const isCustom = customFields[key] || (values[key] && !options.some((o) => o.value === values[key]));

    return (
      <fieldset key={key} className="merge-field">
        <legend>{label}</legend>
        {options.map((option) => (
          <label key={option.value} className={optionClass(!isCustom && values[key] === option.value)}>
            <input
              type="radio"
              name={`merge-${key}`}
              checked={!isCustom && values[key] === option.value}
              onChange={() => {
                setCustomFields((prev) => ({ ...prev, [key]: false }));
                setValue(key, option.value);
              }}
            />
            <span>
              <span className="merge-option-value">{option.value}</span>
              {key === 'email' && isOptedOut(option.value) && (
                <span className="merge-badge merge-badge-off">Emails off</span>
              )}
              <span className="merge-option-source">
                from {option.sources.map((c) => `${c.name} #${c.id} (${countsText(c.counts)})`).join('; ')}
              </span>
            </span>
          </label>
        ))}
        {key !== 'name' && (
          <label className={optionClass(!isCustom && !values[key])}>
            <input
              type="radio"
              name={`merge-${key}`}
              checked={!isCustom && !values[key]}
              onChange={() => {
                setCustomFields((prev) => ({ ...prev, [key]: false }));
                setValue(key, '');
              }}
            />
            <span className="merge-option-source">Leave blank</span>
          </label>
        )}
        <label className={optionClass(!!isCustom)}>
          <input
            type="radio"
            name={`merge-${key}`}
            checked={!!isCustom}
            onChange={() => setCustomFields((prev) => ({ ...prev, [key]: true }))}
          />
          <span className="merge-option-source">Type a new one</span>
        </label>
        {isCustom && (
          <input
            className="merge-input"
            type={key === 'email' ? 'email' : 'text'}
            value={values[key]}
            onChange={(e) => setValue(key, e.target.value)}
            autoFocus
          />
        )}
      </fieldset>
    );
  };

  const renderChoose = () => {
    const extraEmails = additionalEmails(review.customers, keepId, values.email);
    const extraPhones = additionalPhones(review.customers, keepId, values.phone);
    return (
      <>
        <fieldset className="merge-field">
          <legend>Customer to keep</legend>
          <p className="merge-hint">The others are deleted once their records move to this one.</p>
          {review.customers.map((c) => (
            <label key={c.id} className={optionClass(keepId === c.id)}>
              <input type="radio" name="merge-keep" checked={keepId === c.id} onChange={() => chooseKeep(c.id)} />
              <span>
                <span className="merge-option-value">{c.name} #{c.id}</span>
                <span className="merge-option-source">
                  {[c.phone, c.email].filter(Boolean).join(' · ') || 'No phone or email'} — {countsText(c.counts)}
                </span>
              </span>
            </label>
          ))}
        </fieldset>

        {FIELDS.map(renderFieldChoice)}

        <fieldset className="merge-field">
          <legend>Notes</legend>
          <p className="merge-hint">Notes from every customer, combined. Edit as needed.</p>
          <textarea
            className="merge-input"
            rows="4"
            value={values.notes}
            onChange={(e) => setValue('notes', e.target.value)}
          />
        </fieldset>

        <fieldset className="merge-field">
          <legend>Additional phones</legend>
          {extraPhones.length ? (
            <>
              <p className="merge-hint">
                Every other phone number these customers had is kept here as a record. They
                aren&rsquo;t used to match bookings, and can be edited on the customer page.
              </p>
              <ul className="merge-value-list">
                {extraPhones.map((phone) => <li key={phone}>{phone}</li>)}
              </ul>
            </>
          ) : (
            <p className="merge-hint">None.</p>
          )}
        </fieldset>

        <fieldset className="merge-field">
          <legend>Additional emails</legend>
          {extraEmails.length ? (
            <>
              <p className="merge-hint">
                Every other email these customers had is kept here as a record. They aren&rsquo;t
                emailed or used to match bookings, and can be edited on the customer page.
              </p>
              <ul className="merge-value-list">
                {extraEmails.map((email) => (
                  <li key={email}>
                    {email}
                    {isOptedOut(email) && <span className="merge-badge merge-badge-off">Emails off</span>}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="merge-hint">None.</p>
          )}
        </fieldset>
      </>
    );
  };

  const renderReview = () => {
    const merged = review.customers.filter((c) => c.id !== keepId);
    const moving = COUNT_LABELS
      .map(([key, word, plural]) => [merged.reduce((sum, c) => sum + (c.counts[key] || 0), 0), word, plural])
      .filter(([count]) => count > 0)
      .map(([count, word, plural]) => pluralize(count, word, plural));
    const extraEmails = additionalEmails(review.customers, keepId, values.email);
    const extraPhones = additionalPhones(review.customers, keepId, values.phone);
    const optedOutKept = values.email && isOptedOut(values.email);
    const optedOutOthers = extraEmails.filter(isOptedOut);

    return (
      <>
        <div className="merge-summary">
          <h4>Merge {review.customers.length} customers into &ldquo;{values.name}&rdquo;</h4>
          <dl>
            <dt>Moving</dt>
            <dd>{moving.length ? moving.join(', ') : 'Nothing — these customers have no records.'}</dd>
            <dt>Permanently deleting</dt>
            <dd>{merged.map((c) => `“${c.name}” (#${c.id})`).join(', ')}</dd>
            <dt>Kept customer</dt>
            <dd>
              {values.name} #{keepId}
              {values.phone && <> · {values.phone}</>}
              {values.email && <> · {values.email}</>}
              {values.address && <> · {values.address}</>}
            </dd>
            {extraPhones.length > 0 && (
              <>
                <dt>Additional phones</dt>
                <dd>{extraPhones.join(', ')}</dd>
              </>
            )}
            {extraEmails.length > 0 && (
              <>
                <dt>Additional emails</dt>
                <dd>{extraEmails.join(', ')}</dd>
              </>
            )}
          </dl>
        </div>

        {(optedOutKept || optedOutOthers.length > 0 || extraEmails.length > 0) && (
          <ul className="merge-warnings">
            {optedOutKept && (
              <li>
                <strong>{values.email}</strong> has emails turned off. It stays off.
              </li>
            )}
            {optedOutOthers.map((email) => (
              <li key={email}>
                <strong>{email}</strong> has emails turned off. That isn&rsquo;t copied to
                {values.email ? <> <strong>{values.email}</strong>, which will still get emails</> : ' the kept customer'}.
                If this customer asked not to be emailed, turn emails off on the customer page.
              </li>
            ))}
            {extraEmails.length > 0 && (
              <li>
                Website bookings made with {extraEmails.length === 1 ? 'the additional email' : 'an additional email'} won&rsquo;t
                match this customer and may create a new duplicate.
              </li>
            )}
          </ul>
        )}

        <label className="merge-confirm">
          Type <strong>{values.name}</strong> to confirm
          <input
            className="merge-input"
            type="text"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            autoComplete="off"
          />
        </label>
      </>
    );
  };

  const canContinue = review && (values.name || '').trim();
  const confirmed = canContinue && normName(confirmName) === normName(values.name);

  return createPortal(
    <div className="merge-overlay">
      <div className="merge-modal" role="dialog" aria-label="Merge customers">
        <h3>{step === 'review' ? 'Review merge' : 'Merge customers'}</h3>
        {error && <div className="merge-error" role="alert">{error}</div>}

        {loading && <p className="merge-hint">Loading…</p>}
        {review && (step === 'choose' ? renderChoose() : renderReview())}

        <div className="merge-actions">
          <button type="button" className="merge-btn-secondary" onClick={onClose} disabled={merging}>
            Cancel
          </button>
          {review && step === 'review' && (
            <button type="button" className="merge-btn-secondary" onClick={() => { setStep('choose'); setError(''); }} disabled={merging}>
              Back
            </button>
          )}
          {review && step === 'choose' && (
            <button type="button" className="merge-btn-primary" onClick={() => { setStep('review'); setError(''); }} disabled={!canContinue}>
              Review merge
            </button>
          )}
          {review && step === 'review' && (
            <button type="button" className="merge-btn-danger" onClick={handleMerge} disabled={!confirmed || merging}>
              {merging ? 'Merging…' : 'Merge customers permanently'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default MergeCustomersModal;
